import React from 'react';
import classNames from 'clsx';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import ReactPlaceholder from 'react-placeholder/lib/ReactPlaceholder';
import LightbulbOn from 'mdi-material-ui/LightbulbOn';
import RadioboxMarked from 'mdi-material-ui/RadioboxMarked';

import { InlineRadioGroupControlProps } from './InlineRadioGroupControl.interface';
import { useStyles } from './InlineRadioGroupControl.styles';
import { ControlContainer } from 'app/components/ControlContainer';
import { useRadioGroupControl } from 'app/shared/hooks/controls/useRadioGroupControl';
import { FieldsEnum, MessageTypeEnum } from 'app/shared/enums';
import { DictionaryEntry } from 'app/services/dict.service';
import { InputPlaceholder } from 'app/components/InputPlaceholder';
import { Alert } from 'app/components/Alert';

export default function InlineRadioGroupControl(
  props: InlineRadioGroupControlProps
) {
  const classes = useStyles();

  const { input, isLoading, label, width, questionKey } = props;

  const {
    bulbInfoMessage,
    handleChange,
    handleClick,
    renderRadioButtons,
  } = useRadioGroupControl(props);

  const isHorizontalDirection = [
    FieldsEnum.TravelAllTravelersCurrentlyInPoland,
    FieldsEnum.PersonalDataPath,
  ].includes(questionKey as FieldsEnum);

  const renderRadioButton = (entry: DictionaryEntry) => {
    const isActive = entry.key.toString() === input.value.toString();

    return (
      <FormControlLabel
        tabIndex={0}
        value={entry.key.toString()}
        key={entry.key.toString()}
        classes={{
          root: classNames(classes.radioLabelRoot, 'focusVisibleOutline', {
            [classes.radioLabelRootActive]: isActive,
            [classes.radioLabelRootHorizontal]: isHorizontalDirection,
          }),
          label: classNames({
            [classes.radioLabelLabelActive]: isActive,
          }),
        }}
        onKeyDown={(e) => {
          if (
            e.target === e.currentTarget &&
            (e.key === 'Enter' || e.key === ' ')
          ) {
            e.preventDefault();
            handleChange(entry.key.toString());
          }
        }}
        control={
          <Radio
            onClick={handleClick}
            className={classes.radio}
            color="primary"
            checkedIcon={<RadioboxMarked />}
            inputProps={{
              tabIndex: -1,
            }}
          />
        }
        label={entry.value}
      />
    );
  };

  return (
    <ReactPlaceholder
      ready={!isLoading}
      customPlaceholder={<InputPlaceholder width={width} />}
    >
      <ControlContainer
        withoutHtmlFor
        inputId={input.name}
        width={width}
        label={label}
        bottomTipMessage={
          bulbInfoMessage ? (
            <Alert
              text={bulbInfoMessage.content}
              type={MessageTypeEnum.BulbInfo}
              icon={<LightbulbOn />}
            />
          ) : undefined
        }
      >
        <FormControl>
          <RadioGroup
            name={input.name}
            aria-labelledby={input.name}
            id={input.name}
            aria-label={label}
            value={input.value.toString()}
            className={classNames(classes.radioGroup, {
              [classes.verticalDirection]: isHorizontalDirection,
            })}
            onChange={(event, value) => handleChange(value)}
          >
            {renderRadioButtons(renderRadioButton)}
          </RadioGroup>
        </FormControl>
      </ControlContainer>
    </ReactPlaceholder>
  );
}
